/* eslint-disable no-console */
/*
 * EasyBarn — Custom Interior Doors Calculator (Italian range)
 * -----------------------------------------------------------
 * Pricing engine + DOM wiring for the Italian range section.
 * Source of truth: context/custom-interior-doors/pricing-spec.xlsx
 *
 * Dimensions in millimetres. Door only — no hardware bundled.
 */

(function () {
  'use strict';

  // ------------------------------------------------------------
  // Pricing engine (ported from the spec's Pseudocode sheet)
  // ------------------------------------------------------------

  var ITALIAN_STYLES = ['Roma', 'Elba', 'Palermo', 'Adelphi'];

  var TIER_PRICES = {
    1: 1399.00,   // 2032 x 813 (ex stock)
    2: 1599.00,   // 2032 x 762 (short lead)
    3: 2699.00,   // 2400 x 813 (short lead)
    4: 2098.50,   // undersized custom
    5: 2850.00,   // wide custom
    6: 2900.00,   // tall + mid width custom
    7: 3890.00,   // tall + narrow custom
    8: 4822.00    // tall + wide custom
    // Tier 9 (a.k.a. "3+40%") computed at runtime as TIER_PRICES[3] * 1.4 = 3778.60
  };

  var LIMITS = { hMin: 1, hMax: 2700, wMin: 1, wMax: 1800 };

  // The spec calls the derived tier "3+40%" but Shopify option values can't
  // accept that cleanly, so we map it to Tier 9 in the variant map.
  var SPEC_TO_VARIANT_TIER = {
    '1': '1', '2': '2', '3': '3', '4': '4', '5': '5',
    '6': '6', '7': '7', '8': '8', '3+40%': '9'
  };

  var TIER_DESCRIPTIONS = {
    '1':     'Tier 1 — ex stock (2032 × 813)',
    '2':     'Tier 2 — short lead (2032 × 762)',
    '3':     'Tier 3 — short lead (2400 × 813)',
    '4':     'Tier 4 — undersized custom',
    '5':     'Tier 5 — custom wide',
    '6':     'Tier 6 — custom tall',
    '7':     'Tier 7 — custom tall + narrow',
    '8':     'Tier 8 — custom tall + wide',
    '3+40%': 'Tier 9 — custom (Tier 3 size up to 2400 H)'
  };

  function detectItalianTier(h, w) {
    if (h < LIMITS.hMin || h > LIMITS.hMax) return null;
    if (w < LIMITS.wMin || w > LIMITS.wMax) return null;

    // STOCK FIRST (exact match) — must come before custom brackets.
    if (h === 2032 && w === 813) return 1;
    if (h === 2032 && w === 762) return 2;
    if (h === 2400 && w === 813) return 3;

    // CUSTOM BRACKETS
    if (h >= 1    && h <= 2031 && w >= 1    && w <= 813)  return 4;
    if (h >= 1    && h <= 2400 && w >= 814  && w <= 1200) return 5;
    if (h >= 2401 && h <= 2700 && w >= 814  && w <= 1000) return 6;
    if (h >= 2401 && h <= 2700 && w >= 1    && w <= 813)  return 7;
    if (h >= 2401 && h <= 2700 && w >= 1001 && w <= 1800) return 8;

    // DERIVED GAP (Tier 3 + 40%)
    if (h >= 2033 && h <= 2400 && w >= 1 && w <= 813) return '3+40%';

    return null;
  }

  function priceDoorItalian(style, height, width) {
    if (ITALIAN_STYLES.indexOf(style) < 0) return { error: 'Unknown style: ' + style };
    var tier = detectItalianTier(height, width);
    if (tier === null) return { error: 'OUT OF RANGE' };
    var price = tier === '3+40%' ? TIER_PRICES[3] * 1.4 : TIER_PRICES[tier];
    var isStock = tier === 1 || tier === 2 || tier === 3;
    var isDerived = tier === '3+40%';
    return {
      style: style,
      tier: tier,
      tierKey: String(tier),
      variantTierKey: SPEC_TO_VARIANT_TIER[String(tier)] || String(tier),
      tierType: isStock ? 'Stock (exact size)' : isDerived ? 'Custom (derived from Tier 3)' : 'Custom (size-bracket)',
      height: height,
      width: width,
      price: price,
      hardware: null
    };
  }

  window.ebcdPriceDoorItalian = priceDoorItalian;

  // ------------------------------------------------------------
  // Self-tests (the 18 spec test cases)
  // ?ebcd-debug=1 to run on page load.
  // ------------------------------------------------------------

  function runSelfTests() {
    var cases = [
      { id: 1,  style: 'Roma',    h: 2032, w: 813,  tier: 1,        price: 1399    },
      { id: 2,  style: 'Elba',    h: 2032, w: 762,  tier: 2,        price: 1599    },
      { id: 3,  style: 'Palermo', h: 2400, w: 813,  tier: 3,        price: 2699    },
      { id: 4,  style: 'Adelphi', h: 2032, w: 813,  tier: 1,        price: 1399    },
      { id: 5,  style: 'Roma',    h: 2000, w: 800,  tier: 4,        price: 2098.5  },
      { id: 6,  style: 'Roma',    h: 2400, w: 900,  tier: 5,        price: 2850    },
      { id: 7,  style: 'Roma',    h: 2031, w: 1200, tier: 5,        price: 2850    },
      { id: 8,  style: 'Roma',    h: 2500, w: 900,  tier: 6,        price: 2900    },
      { id: 9,  style: 'Roma',    h: 2500, w: 1000, tier: 6,        price: 2900    },
      { id: 10, style: 'Roma',    h: 2500, w: 800,  tier: 7,        price: 3890    },
      { id: 11, style: 'Roma',    h: 2700, w: 1500, tier: 8,        price: 4822    },
      { id: 12, style: 'Roma',    h: 2401, w: 1001, tier: 8,        price: 4822    },
      { id: 13, style: 'Roma',    h: 2200, w: 800,  tier: '3+40%',  price: 3778.6  },
      { id: 14, style: 'Roma',    h: 2400, w: 813,  tier: 3,        price: 2699    },
      { id: 15, style: 'Roma',    h: 2033, w: 813,  tier: '3+40%',  price: 3778.6  },
      { id: 16, style: 'Roma',    h: 1500, w: 1500, error: true },
      { id: 17, style: 'Roma',    h: 2800, w: 800,  error: true },
      { id: 18, style: 'Roma',    h: 2500, w: 1900, error: true }
    ];
    var fails = 0;
    cases.forEach(function (t) {
      var r = priceDoorItalian(t.style, t.h, t.w);
      var ok;
      if (t.error) ok = !!r.error;
      else         ok = r.tier === t.tier && Math.abs(r.price - t.price) < 0.01;
      if (!ok) { fails += 1; console.error('[ebcd-it] test ' + t.id + ' FAILED', { expected: t, got: r }); }
    });
    if (fails === 0) console.log('[ebcd-it] self-tests passed (' + cases.length + '/' + cases.length + ')');
    else             console.warn('[ebcd-it] self-tests: ' + (cases.length - fails) + '/' + cases.length + ' passed, ' + fails + ' failed');
  }

  // ------------------------------------------------------------
  // Formatting
  // ------------------------------------------------------------

  function formatCurrency(value, currency) {
    try {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: currency || 'ZAR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    } catch (e) {
      return 'R ' + value.toFixed(2);
    }
  }

  // ------------------------------------------------------------
  // DOM wiring (one instance per .ebcd-root[data-ebcd-range="italian"])
  // ------------------------------------------------------------

  function initRoot(root) {
    var configEl = root.querySelector('[data-ebcd-config]');
    var config;
    try { config = JSON.parse(configEl.textContent); }
    catch (e) { console.error('[ebcd-it] config parse failed', e); return; }

    var heightEl = root.querySelector('[data-ebcd-height]');
    var widthEl  = root.querySelector('[data-ebcd-width]');
    var qtyEl    = root.querySelector('[data-ebcd-qty]');
    var styleEls = root.querySelectorAll('.ebcd-style-input');
    var resetEl  = root.querySelector('[data-ebcd-reset]');

    var outStyle    = root.querySelector('[data-ebcd-out-style]');
    var outSize     = root.querySelector('[data-ebcd-out-size]');
    var outTier     = root.querySelector('[data-ebcd-out-tier]');
    var outPrice    = root.querySelector('[data-ebcd-out-price]');
    var outPriceEx  = root.querySelector('[data-ebcd-out-price-excl]');
    var outQtyNote  = root.querySelector('[data-ebcd-out-qty-note]');
    var outOfRange  = root.querySelector('[data-ebcd-out-of-range]');
    var ctaAdd      = root.querySelector('[data-ebcd-cta-add]');
    var ctaError    = root.querySelector('[data-ebcd-cta-error]');
    var styleDescs  = root.querySelectorAll('[data-style-desc]');

    function selectedStyle() {
      for (var i = 0; i < styleEls.length; i += 1) {
        if (styleEls[i].checked) return styleEls[i].value;
      }
      return 'Roma';
    }

    function showStyleDesc(style) {
      styleDescs.forEach(function (d) {
        if (d.getAttribute('data-style-desc') === style) d.removeAttribute('hidden');
        else                                              d.setAttribute('hidden', '');
      });
    }

    function lookupVariantId(style, variantTierKey) {
      var map = config.variantMap || {};
      var styleMap = map[style];
      if (!styleMap) return null;
      return styleMap[variantTierKey] || styleMap[String(variantTierKey)] || null;
    }

    function update() {
      var style = selectedStyle();
      var h = parseInt(heightEl.value, 10);
      var w = parseInt(widthEl.value, 10);

      outStyle.textContent = style;
      showStyleDesc(style);

      if (isNaN(h) || isNaN(w)) {
        outSize.textContent = '—';
        outTier.textContent = '—';
        outPrice.textContent = '—';
        if (outPriceEx) outPriceEx.textContent = '';
        outOfRange.hidden = true;
        if (ctaAdd) ctaAdd.disabled = true;
        return;
      }

      outSize.textContent = h + ' × ' + w + ' mm';

      var r = priceDoorItalian(style, h, w);
      if (r.error) {
        outTier.textContent = 'Out of range';
        outPrice.textContent = '—';
        if (outPriceEx) outPriceEx.textContent = '';
        outOfRange.hidden = false;
        if (ctaAdd) ctaAdd.disabled = true;
        return;
      }

      outOfRange.hidden = true;
      outTier.textContent = TIER_DESCRIPTIONS[String(r.tier)] || ('Tier ' + r.tier);

      var qty = Math.max(1, parseInt(qtyEl.value, 10) || 1);
      var lineTotal = r.price * qty;

      outPrice.textContent = formatCurrency(lineTotal, config.currency);
      if (outQtyNote) {
        if (qty > 1) {
          outQtyNote.textContent = formatCurrency(r.price, config.currency) + ' × ' + qty;
          outQtyNote.hidden = false;
        } else {
          outQtyNote.textContent = '';
          outQtyNote.hidden = true;
        }
      }

      if (ctaAdd) {
        var vid = lookupVariantId(style, r.variantTierKey);
        ctaAdd.disabled = !vid;
        ctaAdd.setAttribute('data-variant-id', vid || '');
        if (ctaError && !vid) {
          ctaError.textContent = 'Variant not configured for ' + style + ' / Tier ' + r.variantTierKey + '. Update the Variant ID map.';
          ctaError.hidden = false;
        } else if (ctaError) {
          ctaError.hidden = true;
        }
      }
    }

    function onAddToCart() {
      var style = selectedStyle();
      var h = parseInt(heightEl.value, 10);
      var w = parseInt(widthEl.value, 10);
      var qty = Math.max(1, parseInt(qtyEl.value, 10) || 1);
      var r = priceDoorItalian(style, h, w);
      if (r.error) return;

      var vid = lookupVariantId(style, r.variantTierKey);
      if (!vid) return;

      var body = {
        id: vid,
        quantity: qty,
        properties: {
          Style:  style,
          Height: h + ' mm',
          Width:  w + ' mm'
        }
      };

      ctaAdd.disabled = true;
      var originalLabel = ctaAdd.textContent;
      ctaAdd.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || res.statusText); });
        return res.json();
      }).then(function () {
        if (window.t4s && typeof window.t4s.cartDrawerOpen === 'function') {
          window.t4s.cartDrawerOpen();
        } else {
          window.location.href = '/cart';
        }
      }).catch(function (err) {
        if (ctaError) { ctaError.textContent = 'Could not add to cart: ' + err.message; ctaError.hidden = false; }
      }).finally(function () {
        ctaAdd.disabled = false;
        ctaAdd.textContent = originalLabel;
      });
    }

    function onReset() {
      if (styleEls.length) {
        for (var i = 0; i < styleEls.length; i += 1) styleEls[i].checked = (i === 0);
      }
      heightEl.value = 2032;
      widthEl.value  = 813;
      qtyEl.value    = 1;
      update();
      heightEl.focus();
    }

    heightEl.addEventListener('input', update);
    widthEl.addEventListener('input', update);
    qtyEl.addEventListener('input', update);
    styleEls.forEach(function (el) { el.addEventListener('change', update); });
    if (ctaAdd) ctaAdd.addEventListener('click', onAddToCart);
    if (resetEl) resetEl.addEventListener('click', onReset);

    update();
  }

  function init() {
    document.querySelectorAll('.ebcd-root[data-ebcd-range="italian"]').forEach(initRoot);
    if (/[?&]ebcd-debug=1\b/.test(window.location.search)) runSelfTests();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else                                    init();
})();
